import {controller, attr} from '@github/catalyst'
import {getCookie, setCookie} from './helpers'

/**
 * Hey there! 👋 I bet you are wondering what's going on here.
 *
 * This is a custom element that is used to manage the global banners that are displayed on the site.
 * If you take a look at the `global-banner.ts` code you will see that after closing a banner a cookie is filled with
 * the banner id so that it doesn't show up again. This cookie is called `disabled_global_site_banners` and lasts for
 * 30 days.
 *
 * One cookie can contain 4KB of data, this is roughly 4000 characters which means that we can store around 100 banner
 * ids (roughly). When the cookie is updated it gets renewed for another 30 days, which means there is a possibility
 * that the cookie will never expire if the user keeps updating the cookie. Which in turn means that there is a slight,
 * but a real possibility that the cookie will reach its limit.
 *
 * This custom element is used to manage the cookie and make sure that it doesn't reach its limit. It does this by
 * checking the `banners` attribute and comparing it to the cookie. If the cookie contains banners that are not in the
 * `banners` attribute, it will remove them from the cookie. Simple as that.
 *
 * With love ❤️,
 * @stamat
 */
@controller
class ActiveGlobalBannersElement extends HTMLElement {
  @attr banners: string = ''

  connectedCallback() {
    const activeBanners = this.parseBannersAttribute()
    if (activeBanners.length === 0) return
    const disabledBanners = getCookie('disabled_global_site_banners')?.split(',') || []
    if (disabledBanners.length === 0) return

    const activeBannersMap: Record<string, boolean> = {}
    for (const id of activeBanners) activeBannersMap[id] = true

    const filteredDisabledBanners = disabledBanners.filter(id => activeBannersMap[id])
    if (filteredDisabledBanners.length === disabledBanners.length) return
    setCookie('disabled_global_site_banners', filteredDisabledBanners.join(','), 30)
  }

  parseBannersAttribute() {
    this.banners = this.banners.replace(/\s/g, '')
    return this.banners.split(',')
  }
}
